<template>
  <v-container :class="containerClass" fluid>
    <v-row>
      <v-col cols="12">
        <today-class-schedules
          :items="todaySchedules"
          :loading="isScheduleListLoading"
        ></today-class-schedules>
      </v-col>
      <v-col cols="12">
        <calendar-class-schedules :items="thisWeekSchedules" type="week">
          <template #header> This Week Class Schedules </template>
        </calendar-class-schedules>
      </v-col>
      <v-col cols="12" md="6">
        <classrooms
          :items="endingSoonClasses"
          :loading="isEndingSoonClassListLoading"
        >
          <template #header>Ending Soon Classes</template>
          <template #subheader>No ending soon classes</template>
          <template #schedule="{ item }">
            <div class="mb-2">
              <v-icon left small>mdi-calendar-today</v-icon>
              <strong>
                {{ formatDate(item.start_date, 'DD MMM YYYY') }} -
                {{ formatDate(item.end_date, 'DD MMM YYYY') }}
              </strong>
              <v-chip
                small
                :class="{
                  'mx-1': !$vuetify.breakpoint.mobile,
                  'float-right': $vuetify.breakpoint.mobile,
                }"
              >
                {{ timeFromNow(item.end_date) }}
              </v-chip>
            </div>
            <p>
              Class Meetings ({{ item.subscription_package.class_per_week }} x
              {{ item.subscription_package.minutes_per_class }} mins)
            </p>
            <ul>
              <li v-for="(weeklySchedule, j) in item.weekly_schedules" :key="j">
                {{ dayOfWeekProps(weeklySchedule.day_of_week, 'text') }}
              </li>
            </ul>
          </template>
          <template #nodata>No ending soon classes</template>
        </classrooms>
      </v-col>
      <v-col cols="12" md="6">
        <classrooms
          :items="startingSoonClasses"
          :loading="isStartingSoonClassListLoading"
        >
          <template #header>Starting Soon Classes</template>
          <template #subheader>No starting soon classes</template>
          <template #schedule="{ item }">
            <div class="mb-2">
              <v-icon left small>mdi-calendar-today</v-icon>
              <strong>
                {{ formatDate(item.start_date, 'DD MMM YYYY') }} -
                {{ formatDate(item.end_date, 'DD MMM YYYY') }}
              </strong>
              <v-chip
                small
                :class="{
                  'mx-1': !$vuetify.breakpoint.mobile,
                  'float-right': $vuetify.breakpoint.mobile,
                }"
              >
                {{ timeFromNow(item.start_date) }}
              </v-chip>
            </div>
            <p>
              Class Meetings ({{ item.subscription_package.class_per_week }} x
              {{ item.subscription_package.minutes_per_class }} mins)
            </p>
            <ul>
              <li v-for="(weeklySchedule, j) in item.weekly_schedules" :key="j">
                {{ dayOfWeekProps(weeklySchedule.day_of_week, 'text') }}
              </li>
            </ul>
          </template>
          <template #nodata>No starting soon classes</template>
        </classrooms>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" xl="6">
        <invoices
          :items="pastDueInvoices"
          :loading="isOutstandingInvoiceListLoading"
        >
          <template #header> Past Due Invoices </template>
        </invoices>
      </v-col>
      <v-col cols="12" xl="6">
        <invoices
          :items="willDueInvoices"
          :loading="isOutstandingInvoiceListLoading"
        >
          <template #header> Invoices Due This Week </template>
        </invoices>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import LayoutDashboard from '@layouts/Dashboard'
import { mapGetters, mapActions } from 'vuex'
import TodayClassSchedules from '@components/Dashboard/TodayClassSchedules.vue'
import CalendarClassSchedules from '@components/Dashboard/CalendarClassSchedules.vue'
import Classrooms from '@components/Dashboard/Classrooms.vue'
import Invoices from '@components/Dashboard/Invoices.vue'
export default {
  name: 'Dashboard',
  components: {
    TodayClassSchedules,
    CalendarClassSchedules,
    Classrooms,
    Invoices,
  },
  layout: LayoutDashboard,
  computed: {
    ...mapGetters({
      authUser: 'authUser',
      scheduleList: 'dashboard/classroomSchedule/thisMonthList',
      isScheduleListLoading:
        'dashboard/classroomSchedule/isThisMonthListLoading',
      endingSoonClassList: 'dashboard/classroom/endingSoonList',
      isEndingSoonClassListLoading:
        'dashboard/classroom/isEndingSoonListLoading',
      startingSoonClassList: 'dashboard/classroom/startingSoonList',
      isStartingSoonClassListLoading:
        'dashboard/classroom/isStartingSoonListLoading',
      outstandingInvoiceList: 'dashboard/invoice/outstandingList',
      isOutstandingInvoiceListLoading:
        'dashboard/invoice/isOutstandingListLoading',
    }),
    schedules() {
      return this.scheduleList.data
    },
    todaySchedules() {
      const today = moment()
      return this.schedules.filter((e) => {
        return moment(e.class_date).isSame(today, 'day')
      })
    },
    thisWeekSchedules() {
      const startDate = moment().startOf('week')
      const endDate = moment().endOf('week')
      return this.schedules.filter((e) => {
        const dt = moment(e.class_date)
        return dt.isSameOrAfter(startDate) && dt.isSameOrBefore(endDate)
      })
    },
    endingSoonClasses() {
      return this.endingSoonClassList.data
    },
    startingSoonClasses() {
      return this.startingSoonClassList.data
    },
    outstandingInvoices() {
      return this.outstandingInvoiceList.data
    },
    pastDueInvoices() {
      const today = moment()
      return this.outstandingInvoices.filter(
        (e) => moment(e.due_date).diff(today) < 0
      )
    },
    willDueInvoices() {
      const today = moment()
      return this.outstandingInvoices.filter((e) => {
        const dueDate = moment(e.due_date)
        const diff = dueDate.diff(today)
        return diff >= 0 && diff <= 7
      })
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      getSchedules: 'dashboard/classroomSchedule/getThisMonthList',
      getEndingSoonClasses: 'dashboard/classroom/getEndingSoonList',
      getStartingSoonClasses: 'dashboard/classroom/getStartingSoonList',
      getOutstandingInvoices: 'dashboard/invoice/getOutstandingList',
    }),
    async init() {
      const classSchedulesFilter = {
        student_id: this.authUser.account_id,
        class_status: 'ACTIVE',
      }
      await Promise.all([
        this.getSchedules(classSchedulesFilter),
        this.getEndingSoonClasses(classSchedulesFilter),
        this.getStartingSoonClasses({
          student_id: this.authUser.account_id,
        }),
        this.getOutstandingInvoices({
          student_id: this.authUser.account_id,
        }),
      ])
    },
  },
}
</script>
